import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../../../components/Card';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { getApiCall, patchApiCall } from '../../../service/httpCall';
import { API_CALL_SCRIPT_LIST, API_CALL_SCRIPT_EDIT } from '../../../service/url';
import { ROUTES } from '../../../utilities/constant';

const CallScriptEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle]                       = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription]           = useState('');
  const [fieldErrors, setFieldErrors]           = useState({});
  const [buttonDisable, setButtonDisable]       = useState(false);
  const [loading, setLoading]                   = useState(true);

  useEffect(() => {
    if (!id) return;

    getApiCall(API_CALL_SCRIPT_LIST)
      .then((resp) => {
        const list = Array.isArray(resp) ? resp : resp?.data ?? [];
        const found = list.find((item) => String(item.id) === String(id));

        if (found) {
          setTitle(found.title || '');
          setShortDescription(found.short_description || '');
          setDescription(found.description || '');
        } else {
          toast.error('Script not found');
          navigate(`/${ROUTES.CALL_SCRIPT.LIST}`);
        }
      })
      .catch(() => toast.error("Can't load script data"))
      .finally(() => setLoading(false));
  }, [id]);

  const validate = () => {
    const errors = {};
    if (!title.trim())            errors.title            = 'Title is required';
    if (!shortDescription.trim()) errors.shortDescription = 'Short description is required';
    if (!description.trim())      errors.description      = 'Description is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitForm = () => {
    if (!validate()) return;
    if (!id) return toast.error('ID missing!');

    setButtonDisable(true);

    patchApiCall(API_CALL_SCRIPT_EDIT.replace(':id', id), {
      title,
      short_description: shortDescription,
      description,
    })
      .then(() => {
        toast.success('Script updated successfully');
        navigate(`/${ROUTES.CALL_SCRIPT.LIST}`);
      })
      .catch((err) => {
        toast.error(err || 'Update unsuccessful');
        setButtonDisable(false);
      });
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <Row>
      <Col xl="12">
        <Card>
          <Card.Header>
            <h4>Edit Call Script</h4>
          </Card.Header>
          <Card.Body>
            <Form>
              <Row>

            
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>
                    Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (e.target.value.trim())
                        setFieldErrors((prev) => ({ ...prev, title: '' }));
                    }}
                    placeholder="Enter script title"
                    isInvalid={!!fieldErrors.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.title}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>
                    Short Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={shortDescription}
                    onChange={(e) => {
                      setShortDescription(e.target.value);
                      if (e.target.value.trim())
                        setFieldErrors((prev) => ({ ...prev, shortDescription: '' }));
                    }}
                    placeholder="Enter short description"
                    isInvalid={!!fieldErrors.shortDescription}
                  />
                  <Form.Control.Feedback type="invalid">
                    {fieldErrors.shortDescription}
                  </Form.Control.Feedback>
                </Form.Group>

              
                <Form.Group className="col-md-12 mb-3">
                  <Form.Label
                    style={{ fontWeight: 500, color: '#495057', marginBottom: '0.5rem' }}
                  >
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <div
                    style={{
                      border: fieldErrors.description
                        ? '1px solid red'
                        : '1px solid #ced4da',
                      borderRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    <CKEditor
                      editor={ClassicEditor}
                      data={description}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setDescription(data);
                        if (data.trim())
                          setFieldErrors((prev) => ({ ...prev, description: '' }));
                      }}
                      onReady={(editor) => {
                        editor.editing.view.change((writer) => {
                          writer.setStyle(
                            'height',
                            '300px',
                            editor.editing.view.document.getRoot()
                          );
                        });
                      }}
                    />
                  </div>
                  {fieldErrors.description && (
                    <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '4px' }}>
                      {fieldErrors.description}
                    </div>
                  )}
                </Form.Group>

              </Row>

              <Button onClick={submitForm} disabled={buttonDisable}>
                {buttonDisable ? 'Updating...' : 'Update'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default CallScriptEdit;